import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface User {
  _id: string;
  email: string;
  userType: 'user' | 'admin';
  createdAt: string;
  lastLogin?: string;
  country?: string;
  currency?: string;
  defaultFifthDoseEnabled?: boolean;
}

const AdminUserManagementPage: React.FC = () => {
  const { isAuthenticated, userType } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [newUserType, setNewUserType] = useState<'user' | 'admin'>('user');

  // Only allow admin users
  const isAdmin = userType === 'admin';

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    fetchUsers();
  }, [isAuthenticated, isAdmin]);

  const fetchUsers = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserTypeChange = async (email: string, newType: 'user' | 'admin') => {
    const token = localStorage.getItem('token');
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/user-type`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email,
          userType: newType
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update user type');
      }
      
      setSuccess(`User ${email} updated to ${newType} successfully`);
      setEditingUser(null);
      await fetchUsers(); // Refresh the list
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return <div className="p-8 text-center">Please log in as an admin to access this page.</div>;
  }
  
  if (!isAdmin) {
    return <div className="p-8 text-center text-red-600">Access denied. Admins only.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-8">
      <h1 className="text-4xl font-bold mb-8 font-['Space_Grotesk'] bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Admin: User Management</h1>
      
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : error ? (
        <div className="text-red-600 mb-4 p-4 bg-red-50 rounded">{error}</div>
      ) : (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-stone-900 font-['Space_Grotesk'] mb-4">All Users ({users.length})</h2>
            <p className="text-gray-600 mb-4">
              Manage user accounts and admin privileges. Be careful when changing user types.
            </p>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-emerald-50">
                    <th className="p-4 text-left border-b font-semibold text-emerald-800 uppercase tracking-wider">Email</th>
                    <th className="p-4 text-left border-b font-semibold text-emerald-800 uppercase tracking-wider">User Type</th>
                    <th className="p-4 text-left border-b font-semibold text-emerald-800 uppercase tracking-wider">Country</th>
                    <th className="p-4 text-left border-b font-semibold text-emerald-800 uppercase tracking-wider">Currency</th>
                    <th className="p-4 text-left border-b font-semibold text-emerald-800 uppercase tracking-wider">Joined</th>
                    <th className="p-4 text-left border-b font-semibold text-emerald-800 uppercase tracking-wider">Last Login</th>
                    <th className="p-4 text-left border-b font-semibold text-emerald-800 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id} className="border-b hover:bg-gray-50">
                      <td className="p-4 font-medium">{user.email}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.userType === 'admin' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.userType}
                        </span>
                      </td>
                      <td className="p-4 text-gray-600">{user.country || '-'}</td>
                      <td className="p-4 text-gray-600">{user.currency || '-'}</td>
                      <td className="p-4 text-gray-600">{formatDate(user.createdAt)}</td>
                      <td className="p-4 text-gray-600">{user.lastLogin ? formatDate(user.lastLogin) : 'Never'}</td>
                      <td className="p-4">
                        {editingUser === user._id ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={newUserType}
                              onChange={(e) => setNewUserType(e.target.value as 'user' | 'admin')}
                              className="border rounded px-2 py-1 text-sm"
                            >
                              <option value="user">user</option>
                              <option value="admin">admin</option>
                            </select>
                            <button
                              onClick={() => handleUserTypeChange(user.email, newUserType)}
                              disabled={loading || newUserType === user.userType}
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:bg-gray-400"
                            >
                              {loading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => setEditingUser(null)}
                              disabled={loading}
                              className="bg-gray-400 text-white px-3 py-1 rounded text-sm hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingUser(user._id);
                              setNewUserType(user.userType);
                            }}
                            className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                          >
                            Change Type
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Total Users</h3>
              <p className="text-3xl font-bold text-purple-600">{users.length}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Admin Users</h3>
              <p className="text-3xl font-bold text-purple-600">
                {users.filter(u => u.userType === 'admin').length}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Regular Users</h3>
              <p className="text-3xl font-bold text-purple-600">
                {users.filter(u => u.userType === 'user').length}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded shadow-lg z-50">
          {success}
        </div>
      )}
    </div>
  );
};

export default AdminUserManagementPage; 